import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, notificationsAPI, participantsAPI, testsAPI } from '../services/api';
import Loading from '../components/Loading';
import StoryGenerator from '../components/StoryGenerator';
import ResultsStoryGenerator from '../components/ResultsStoryGenerator';
import StitchHeader from '../components/StitchHeader';
import '../styles/Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tests, setTests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showStoryGenerator, setShowStoryGenerator] = useState(false);
  const [selectedTestForStory, setSelectedTestForStory] = useState(null);
  const [showResultsStory, setShowResultsStory] = useState(false);
  const [selectedTestForResults, setSelectedTestForResults] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [statsResponse, testsResponse, notificationsResponse] = await Promise.all([
        dashboardAPI.getStats(),
        testsAPI.list(),
        notificationsAPI.list()
      ]);

      if (statsResponse.data.success) setStats(statsResponse.data.stats);
      if (testsResponse.data.success) setTests(testsResponse.data.tests);
      if (notificationsResponse.data.success) setNotifications(notificationsResponse.data.notifications);
    } catch (requestError) {
      console.error('Dashboard error:', requestError);
      setError(requestError.error || 'خطا در بارگذاری اطلاعات');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این آزمون را حذف کنید؟')) return;

    try {
      const response = await testsAPI.delete(testId);
      if (response.data.success) {
        setTests((currentTests) => currentTests.filter((test) => test.id !== testId));
        fetchDashboardData();
      }
    } catch (requestError) {
      alert(requestError.error || 'خطا در حذف آزمون');
    }
  };

  const handleCopyLink = async (uuid) => {
    const link = `${window.location.origin}/t/${uuid}`;
    try {
      await navigator.clipboard.writeText(link);
      alert('لینک کپی شد.');
    } catch (copyError) {
      console.error('Copy link error:', copyError);
      alert(link);
    }
  };

  const getShareText = (testType) => {
    const texts = {
      friend: '🎯 ببین چقدر منو می‌شناسی! یه تست جالب درست کردم، حتماً شرکت کن:',
      spouse: '💖 عشقم، ببین چقدر منو می‌شناسی! یه تست خاص برات درست کردم:',
      family: '👨‍👩‍👧 خانواده عزیزم، چقدر منو می‌شناسید؟ یه تست خانوادگی درست کردم:',
      colleague: '💼 همکار عزیز، ببین چقدر منو می‌شناسی! یه تست شغلی درست کردم:',
      newFriend: '👋 سلام! بیا بهتر باهم آشنا بشیم. یه تست جالب درست کردم:',
      crush: '💘 ببین چقدر منو می‌شناسی! یه تست ویژه درست کردم، حتماً شرکت کن:',
      classmate: '📚 هم‌کلاسی عزیز! چقدر منو می‌شناسی؟ یه تست جالب آماده کردم:',
      follower: '📱 سلام فالوور عزیز! ببین چقدر منو می‌شناسی:'
    };
    return texts[testType] || texts.friend;
  };

  const handleShareLink = async (uuid, testType) => {
    const link = `${window.location.origin}/t/${uuid}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'چقدر منو می‌شناسی؟', text: getShareText(testType), url: link });
      } catch (shareError) {
        if (shareError.name !== 'AbortError') console.error('Share error:', shareError);
      }
      return;
    }
    handleCopyLink(uuid);
  };

  const getTestTypeLabel = (type) => {
    const labels = { friend: 'دوست صمیمی', spouse: 'همسر/نامزد', family: 'خانواده', colleague: 'همکار', newFriend: 'دوست جدید', crush: 'کراش', classmate: 'هم‌کلاسی', follower: 'فالوور' };
    return labels[type] || type || 'آزمون شخصی';
  };

  const getTestIcon = (type) => ({ friend: 'group', spouse: 'favorite', family: 'family_restroom', colleague: 'work', newFriend: 'waving_hand', crush: 'favorite', classmate: 'school', follower: 'smartphone' }[type] || 'quiz');

  const getScoreColor = (score) => {
    if (score >= 80) return '#006e4b';
    if (score >= 60) return '#3525cd';
    if (score >= 40) return '#b7791f';
    return '#ba1a1a';
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markRead();
      setNotifications((currentNotifications) => currentNotifications.map((notification) => ({ ...notification, is_read: true })));
    } catch (requestError) {
      console.error('Mark read error:', requestError);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await notificationsAPI.delete(notificationId);
      setNotifications((currentNotifications) => currentNotifications.filter((notification) => notification.id !== notificationId));
    } catch (requestError) {
      console.error('Delete notification error:', requestError);
    }
  };

  const handleResultsStory = async (test) => {
    try {
      const response = await participantsAPI.getResults(test.id);
      if (response.data.success) {
        setSelectedTestForResults({
          ...test,
          topParticipants: response.data.participants.slice(0, 3),
          totalParticipants: response.data.stats.total,
          averageScore: response.data.stats.average
        });
        setShowResultsStory(true);
      }
    } catch (requestError) {
      alert('خطا در دریافت نتایج');
    }
  };

  if (loading) return <Loading message="در حال بارگذاری داشبورد..." />;
  if (error) return <div className="dashboard-error"><p>{error}</p><button onClick={fetchDashboardData} type="button">تلاش مجدد</button></div>;

  const unreadCount = notifications.filter((notification) => !notification.is_read).length;
  const latestTest = tests[0];

  return (
    <div className="dashboard-container">
      <StitchHeader onNotifications={() => setShowNotifications((visible) => !visible)} unreadCount={unreadCount} />
      <main className="dashboard-main">
        <section className="dashboard-welcome">
          <div>
            <p>سلام، {user?.name || 'دوست من'} 👋</p>
            <h1>داشبورد مدیریت آزمون‌ها</h1>
          </div>
          <button className="dashboard-create-button" onClick={() => navigate('/create')} type="button"><span className="material-symbols-outlined">add_circle</span>ایجاد آزمون جدید</button>
        </section>

        {showNotifications && (
          <section className="notifications-panel">
            <div className="notifications-header"><h2>اعلان‌ها</h2>{unreadCount > 0 && <button onClick={handleMarkAllRead} type="button">خواندن همه</button>}</div>
            <div className="notifications-list">
              {notifications.length === 0 ? <p className="no-notifications">اعلانی وجود ندارد.</p> : notifications.map((notification) => (
                <article key={notification.id} className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}><div><h3>{notification.title}</h3><p>{notification.message}</p><small>{new Date(notification.created_at).toLocaleString('fa-IR')}</small></div><button onClick={() => handleDeleteNotification(notification.id)} type="button" aria-label="حذف اعلان"><span className="material-symbols-outlined">close</span></button></article>
              ))}
            </div>
          </section>
        )}

        {stats && (
          <section className="dashboard-stat-grid">
            <article className="dashboard-stat-card dashboard-stat-primary"><div><span>آزمون‌های ساخته‌شده</span><i className="material-symbols-outlined">quiz</i></div><strong>{stats.total_tests.toLocaleString('fa-IR')}</strong></article>
            <article className="dashboard-stat-card dashboard-stat-secondary"><div><span>تعداد شرکت‌کنندگان</span><i className="material-symbols-outlined">groups</i></div><strong>{stats.total_participants.toLocaleString('fa-IR')}</strong></article>
            <article className="dashboard-stat-card dashboard-stat-tertiary"><div><span>میانگین امتیاز</span><i className="material-symbols-outlined">analytics</i></div><strong>{stats.average_score.toLocaleString('fa-IR')}٪</strong></article>
            <article className="dashboard-stat-card dashboard-stat-gold"><div><span>بالاترین امتیاز</span><i className="material-symbols-outlined">trophy</i></div><strong>{stats.highest_score.toLocaleString('fa-IR')}٪</strong></article>
          </section>
        )}

        <section className="dashboard-tests-section">
          <div className="dashboard-section-heading"><div><h2>آزمون‌های فعال</h2><p>{tests.length.toLocaleString('fa-IR')} آزمون در حساب شما</p></div>{latestTest && <span>آخرین آزمون: {new Date(latestTest.created_at).toLocaleDateString('fa-IR')}</span>}</div>

          {tests.length === 0 ? (
            <div className="no-tests"><span className="material-symbols-outlined">add_task</span><h3>هنوز آزمونی نساختی</h3><p>با ساخت اولین آزمون، دوستات را به یک بازی جذاب دعوت کن.</p><button onClick={() => navigate('/create')} type="button">ساخت آزمون جدید</button></div>
          ) : (
            <div className="dashboard-test-tiles">
              {tests.map((test) => (
                <article className="dashboard-test-tile" key={test.id}>
                  <div className="dashboard-tile-info">
                    <span className="dashboard-tile-icon material-symbols-outlined">{getTestIcon(test.test_type)}</span>
                    <div className="dashboard-tile-copy">
                      <div className="dashboard-tile-title"><strong>{getTestTypeLabel(test.test_type)}</strong><span className="dashboard-status">فعال</span></div>
                      <div className="dashboard-tile-meta"><span><i className="material-symbols-outlined">calendar_today</i>{new Date(test.created_at).toLocaleDateString('fa-IR')}</span><span><i className="material-symbols-outlined">groups</i>{(test.participants_count || 0).toLocaleString('fa-IR')} شرکت‌کننده</span>{test.top_score && <span><i className="material-symbols-outlined">star</i><b style={{ color: getScoreColor(test.top_score) }}>{test.top_score.toLocaleString('fa-IR')}٪</b> برتر</span>}</div>
                    </div>
                  </div>
                  <div className="dashboard-tile-actions">
                    <button onClick={() => navigate(`/test/${test.id}`)} type="button" title="مشاهده نتایج"><span className="material-symbols-outlined">bar_chart</span></button>
                    <button onClick={() => handleShareLink(test.uuid, test.test_type)} type="button" title="اشتراک‌گذاری"><span className="material-symbols-outlined">share</span></button>
                    <button onClick={() => handleCopyLink(test.uuid)} type="button" title="کپی لینک"><span className="material-symbols-outlined">content_copy</span></button>
                    <button onClick={() => { setSelectedTestForStory(test); setShowStoryGenerator(true); }} type="button" title="ساخت استوری"><span className="material-symbols-outlined">auto_awesome</span></button>
                    <button onClick={() => handleResultsStory(test)} type="button" title="استوری نتایج"><span className="material-symbols-outlined">emoji_events</span></button>
                    <button onClick={() => handleDeleteTest(test.id)} className="dashboard-delete-action" type="button" title="حذف آزمون"><span className="material-symbols-outlined">delete</span></button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {showStoryGenerator && selectedTestForStory && <StoryGenerator testUuid={selectedTestForStory.uuid} creatorName={user?.name} testType={selectedTestForStory.test_type} questionsCount={10} onClose={() => { setShowStoryGenerator(false); setSelectedTestForStory(null); }} />}
      {showResultsStory && selectedTestForResults && <ResultsStoryGenerator testType={selectedTestForResults.test_type} topParticipants={selectedTestForResults.topParticipants} totalParticipants={selectedTestForResults.totalParticipants} averageScore={selectedTestForResults.averageScore} creatorName={user?.name} onClose={() => { setShowResultsStory(false); setSelectedTestForResults(null); }} />}
    </div>
  );
}

export default Dashboard;
